import React, { useState, useEffect } from 'react';
import { T, ThemeToggle } from '../tema';
import { LOGO_SRC } from './Admin';
import {
  erNativeBiometriTilgaengelig,
  tjekBiometriskMedPluginGuard,
  tjekBiometrisk,
  gemCredentialsMedBiometri,
  hentCredentialsMedBiometri,
  harGemteCredentials,
} from '../biometri-native';

// Dynamisk import af @simplewebauthn/browser — kun indlæst på enheder der understøtter WebAuthn
async function hent_passkey_browser() {
  const mod = await import('@simplewebauthn/browser');
  return mod;
}

function LoginSkærm({ onLogin }) {
 const [email, setEmail] = useState("");
 const [adgangskode, setAdgangskode] = useState("");
 const [vis_pw, setVisPw] = useState(false);
 const [fejl, setFejl] = useState("");
 const [loader, setLoader] = useState(false);
 // Tilstande: null=login, '2fa'=verificer, 'setup_valg'=vælg metode, 'setup_app'=scan QR, 'setup_email'=email kode, 'setup_bekraeft'=indtast kode, 'recovery_koder'=vis koder
 const [trin, setTrin] = useState(null);
 const [to_fa_kode, setToFaKode] = useState("");
 const [token_data, setTokenData] = useState(null); // challenge_token eller setup_token
 const [qr_data, setQrData] = useState(null); // { qr_data_url, hemmelighed_visning }
 const [email_maskeret, setEmailMaskeret] = useState("");
 const [recovery_koder, setRecoveryKoder] = useState([]);
 const [valgt_2fa_metode, setValgt2faMetode] = useState('app'); // 'app' | 'email' | 'recovery'
 const [kopieret, setKopieret] = useState(false);

 // ── PASSKEY / BIOMETRI DETEKTERING ──
 // Web: window.PublicKeyCredential (fungerer i desktop-browser)
 // Android native: Capacitor platform check (window.PublicKeyCredential er undefined i WebView)
 //
 // erNativeBiometriTilgaengelig() er en SYNC platform-check — ikke et plugin-check.
 // Den returnerer true på ALLE native Android Capacitor-builds, inkl. APKs uden plugin (v1.2.0).
 // Bruges kun til at afgøre om vi skal køre async-initialiseringen nedenfor.
 // Brug aldrig denne alene til at aktivere biometri-UI.
 const er_native_android = erNativeBiometriTilgaengelig();

 // er_native_plugin_klar sættes true KUN hvis tjekBiometriskMedPluginGuard() bekræfter
 // at @capgo/capacitor-native-biometric er installeret i den aktuelle APK.
 // I v1.2.0 APK forbliver den false — og hele native biometri-UI vises ikke.
 const [er_native_plugin_klar, setErNativePluginKlar] = useState(false);

 const [passkey_understoettes] = useState(() =>
   er_native_android
     ? false  // afgøres endeligt af er_native_plugin_klar via useEffect
     : typeof window !== 'undefined' && !!window.PublicKeyCredential
 );
 const [passkey_loader, setPasskeyLoader] = useState(false);

 // Native Android: tjek om biometri er konfigureret OG om credentials er gemt
 const [native_biometri_klar, setNativeBiometriKlar] = useState(false);
 const [vis_gem_biometri, setVisGemBiometri] = useState(false); // vises efter vellykket login
 const [gem_biometri_loader, setGemBiometriLoader] = useState(false);

 useEffect(() => {
   if (!er_native_android) return;
   // tjekBiometriskMedPluginGuard() verificerer at plugin faktisk er installeret i APK'en.
   // Returnerer altid { tilgaengelig: false } hvis plugin mangler (v1.2.0) — crasher aldrig.
   (async () => {
     try {
       const { tilgaengelig } = await tjekBiometriskMedPluginGuard();
       if (!tilgaengelig) return; // plugin ikke installeret eller biometri ikke konfigureret
       setErNativePluginKlar(true);
       const har = await harGemteCredentials();
       setNativeBiometriKlar(har); // fingeraftryk-knap vises kun hvis credentials er gemt
     } catch {
       // ingen biometri tilgængelig — ignorer, app kører normalt
     }
   })();
 }, [er_native_android]);

 const nulstil = () => { setTrin(null); setToFaKode(""); setFejl(""); setTokenData(null); setQrData(null); setRecoveryKoder([]); setKopieret(false); };

 // ── NATIVE ANDROID BIOMETRI LOGIN (fingeraftryk) ──
 // Bruges i stedet for WebAuthn når vi kører som Capacitor APK på Android.
 // Henter krypterede credentials fra Android Keystore efter fingeraftryk-bekræftelse.
 const forsøg_biometri_login = async () => {
  setFejl(""); setPasskeyLoader(true);
  try {
   const { email: gemt_email, adgangskode: gemt_pw } = await hentCredentialsMedBiometri();
   // Genrug normal login-flow med de hentede credentials
   const res = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: gemt_email, adgangskode: gemt_pw }),
   });
   const data = await res.json();
   setPasskeyLoader(false);
   if (!res.ok) {
    setFejl(data.fejl || "Biometri-login mislykkedes — prøv med adgangskode");
    return;
   }
   if (data.setup_2fa_krævet) {
    setTokenData(data); setTrin('setup_valg');
   } else if (data.to_faktor_krævet) {
    setTokenData(data); setTrin('2fa');
   } else {
    fuldfør_login(data);
   }
  } catch (err) {
   setPasskeyLoader(false);
   const msg = err?.message || "";
   if (msg.includes("cancel") || msg.includes("Cancel") || msg.includes("USER_CANCEL")) {
    setFejl("Fingeraftryk-login annulleret");
   } else if (msg.includes("BIOMETRICS_NOT_ENROLLED")) {
    setFejl("Ingen fingeraftryk registreret på denne enhed");
   } else {
    setFejl("Fingeraftryk-login fejlede — prøv med adgangskode");
   }
  }
 };

 // Gem credentials til fremtidigt fingeraftryk-login (tilbydes efter vellykket login)
 const gem_til_biometri = async (gem) => {
  const pending = sessionStorage.getItem("_sv_pending_bruger");
  sessionStorage.removeItem("_sv_pending_bruger");
  const bruger = pending ? JSON.parse(pending) : null;
  setVisGemBiometri(false);
  if (gem && email && adgangskode) {
   setGemBiometriLoader(true);
   try {
    await gemCredentialsMedBiometri(email, adgangskode);
    setNativeBiometriKlar(true);
   } catch {
    // Gem fejlede — log ind alligevel
   } finally {
    setGemBiometriLoader(false);
   }
  }
  if (bruger) onLogin(bruger);
 };

 // ── PASSKEY LOGIN (WebAuthn — web-browser) ──
 const forsøg_passkey_login = async () => {
  if (!passkey_understoettes) return;
  setFejl(""); setPasskeyLoader(true);
  try {
   // Trin 1: Hent challenge fra backend (send email hvis udfyldt, ellers usernameless)
   const start_res = await fetch("/api/auth/passkey/login-start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim() || undefined })
   });
   if (!start_res.ok) {
    const d = await start_res.json();
    setFejl(d.fejl || "Kunne ikke starte passkey-login");
    setPasskeyLoader(false);
    return;
   }
   const options = await start_res.json();

   // Trin 2: Bed browser/autentikator om assertion
   const { startAuthentication } = await hent_passkey_browser();
   let assertion;
   try {
    assertion = await startAuthentication({ optionsJSON: options });
   } catch (err) {
    // Bruger annullerede eller autentikator ikke tilgængelig
    if (err.name === 'NotAllowedError') {
     setFejl("Passkey-login annulleret");
    } else {
     setFejl("Ingen passkey fundet på denne enhed");
    }
    setPasskeyLoader(false);
    return;
   }

   // Trin 3: Send assertion til backend
   const faerdig_res = await fetch("/api/auth/passkey/login-faerdig", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ response: assertion })
   });
   const data = await faerdig_res.json();
   setPasskeyLoader(false);
   if (!faerdig_res.ok) {
    setFejl(data.fejl || "Passkey-login mislykkedes");
    return;
   }
   fuldfør_login(data);
  } catch (err) {
   setPasskeyLoader(false);
   setFejl("Passkey-login fejlede — prøv med adgangskode");
  }
 };
 const send_reset_email = async () => {
 setFejl(""); setLoader(true);
 try {
 const res = await fetch("/api/auth/reset-anmod", {
 method: "POST",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify({ email })
 });
 setLoader(false);
 if (!res.ok) { const d = await res.json(); setFejl(d.fejl || "Fejl"); return; }
 setTrin('reset_sendt');
 } catch (err) { setLoader(false); setFejl("Serverfejl"); }
 };
 const fuldfør_login = (data) => {
 if (data.exp) sessionStorage.setItem("sv_token_exp", String(data.exp * 1000));
 sessionStorage.setItem("sv_bruger", JSON.stringify(data.bruger));
 if (data.recovery_koder) {
 setRecoveryKoder(data.recovery_koder);
 setTrin('recovery_koder');
 } else {
 // På native Android (med plugin installeret): tilbyd at gemme fingeraftryk-login
 // er_native_plugin_klar garanterer at plugin er i APK — ingen crash i v1.2.0
 if (er_native_plugin_klar && !native_biometri_klar && email && adgangskode) {
  // tjekBiometrisk() er sikker fordi er_native_plugin_klar allerede er verificeret
  tjekBiometrisk().then(({ tilgaengelig }) => {
   if (tilgaengelig) setVisGemBiometri(true);
   else onLogin(data.bruger);
  }).catch(() => onLogin(data.bruger));
  // Gem bruger-data midlertidigt så gem_til_biometri kan bruge dem
  sessionStorage.setItem("_sv_pending_bruger", JSON.stringify(data.bruger));
  return; // afvent brugervalg om gem-til-biometri
 }
 onLogin(data.bruger);
 }
 };
 const forsøg_login = async () => {
 setFejl("");
 setLoader(true);
 try {
 const res = await fetch("/api/auth/login", {
 method: "POST",
 credentials: "include",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify({ email, adgangskode })
 });
 const data = await res.json();
 setLoader(false);
 if (!res.ok) {
 setFejl(data.fejl || "Forkert email eller adgangskode");
 return;
 }
 if (data.setup_2fa_krævet) {
 // 2FA krævet men ikke opsat → opsætningswizard
 setTokenData(data);
 setTrin('setup_valg');
 } else if (data.to_faktor_krævet) {
 // 2FA opsat → verificer
 setTokenData(data);
 setTrin('2fa');
 } else {
 fuldfør_login(data);
 }
 } catch (err) {
 setLoader(false);
 setFejl("Kunne ikke forbinde til serveren");
 }
 };
 // ── 2FA SETUP: Vælg metode og start opsætning ──
 const start_setup = async (metode) => {
 setFejl(""); setLoader(true);
 try {
 const res = await fetch("/api/auth/2fa-setup", {
 method: "POST",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify({ setup_token: token_data.setup_token, metode })
 });
 const data = await res.json();
 setLoader(false);
 if (!res.ok) { setFejl(data.fejl); return; }
 if (metode === 'app') {
 setQrData(data);
 setTrin('setup_app');
 } else {
 setEmailMaskeret(data.email);
 setTrin('setup_email');
 }
 } catch (err) { setLoader(false); setFejl("Serverfejl"); }
 };
 // ── 2FA SETUP: Bekræft kode (app eller email) ──
 const bekræft_setup = async () => {
 if (to_fa_kode.length !== 6) { setFejl("Koden skal være 6 cifre"); return; }
 setFejl(""); setLoader(true);
 try {
 const res = await fetch("/api/auth/2fa-setup-bekraeft", {
 method: "POST",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify({ setup_token: token_data.setup_token, kode: to_fa_kode })
 });
 const data = await res.json();
 setLoader(false);
 if (!res.ok) { setFejl(data.fejl || "Forkert kode"); return; }
 fuldfør_login(data);
 } catch (err) { setLoader(false); setFejl("Serverfejl"); }
 };
 // ── 2FA LOGIN: Verificer kode ──
 const bekræft_2fa = async () => {
 const min_laengde = valgt_2fa_metode === 'recovery' ? 8 : 6;
 if (to_fa_kode.length < min_laengde) { setFejl(valgt_2fa_metode === 'recovery' ? "Recovery-koden skal være 8 tegn" : "Koden skal være 6 cifre"); return; }
 setFejl(""); setLoader(true);
 try {
 const res = await fetch("/api/auth/2fa-verificer", {
 method: "POST",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify({ challenge_token: token_data.challenge_token, kode: to_fa_kode, metode: valgt_2fa_metode })
 });
 const data = await res.json();
 setLoader(false);
 if (!res.ok) {
 setFejl(data.fejl || "Forkert kode");
 if (res.status === 429) nulstil();
 return;
 }
 if (data.exp) sessionStorage.setItem("sv_token_exp", String(data.exp * 1000));
 sessionStorage.setItem("sv_bruger", JSON.stringify(data.bruger));
 onLogin(data.bruger);
 } catch (err) {
 setLoader(false);
 setFejl("Kunne ikke forbinde til serveren");
 }
 };
 // ── Send email-kode ved login ──
 const send_email_kode = async () => {
 setFejl(""); setLoader(true);
 try {
 const res = await fetch("/api/auth/2fa-send-email", {
 method: "POST", headers: { "Content-Type": "application/json" },
 body: JSON.stringify({ challenge_token: token_data.challenge_token })
 });
 const data = await res.json();
 setLoader(false);
 if (!res.ok) { setFejl(data.fejl); return; }
 setEmailMaskeret(data.email);
 setValgt2faMetode('email');
 } catch (err) { setLoader(false); setFejl("Serverfejl"); }
 };
 const panel = {
 background:"rgba(6,9,26,0.92)",
 border:"1px solid rgba(255,255,255,0.1)",
 borderTop:"1px solid rgba(255,255,255,0.18)",
 borderRadius:20,
 backdropFilter:"blur(28px)", WebkitBackdropFilter:"blur(28px)",
 padding:"clamp(20px, 5vw, 36px) clamp(16px, 4vw, 32px)",
 boxShadow:"0 24px 64px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.06) inset",
 };
 const fejl_boks = fejl ? (
 <div role="alert" style={{ background:"rgba(255,107,107,0.1)", border:"1px solid rgba(255,107,107,0.3)", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:13, color:T.roed }}>{fejl}</div>
 ) : null;
 const kode_input = (onEnter) => {
 const er_recovery = valgt_2fa_metode === 'recovery';
 return (
 <input type="text"
 inputMode={er_recovery ? "text" : "numeric"}
 pattern={er_recovery ? undefined : "[0-9]*"}
 value={to_fa_kode}
 onChange={e => {
 const v = er_recovery
 ? e.target.value.toUpperCase().slice(0, 8)
 : e.target.value.replace(/\D/g,'').slice(0, 6);
 setToFaKode(v);
 }}
 onKeyDown={e=>e.key==="Enter"&&onEnter()}
 placeholder={er_recovery ? "XXXXXXXX" : "000000"}
 autoFocus
 className="sv-input"
 style={{ width:"100%", background:"rgba(8,12,30,0.65)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:9, padding:"16px", color:T.creme, fontSize:24, textAlign:"center", letterSpacing:"0.3em", fontFamily:"DM Mono, monospace", boxSizing:"border-box", marginBottom:16 }}
 />
 );
 };
 const turkis_knap = (tekst, onClick, disabled, tooltip) => (
 <button onClick={onClick} disabled={disabled||loader}
 className={disabled||loader ? "sv-login-knap sv-login-knap-disabled" : "sv-login-knap"}
 title={disabled && tooltip ? tooltip : undefined}
 aria-disabled={disabled||loader ? "true" : undefined}
 style={{ width:"100%",
 background:loader||disabled ? "rgba(79,70,229,0.4)" : "linear-gradient(135deg,#4f46e5 0%,#3fd4c7 100%)",
 border:"none", borderRadius:11, padding:"15px", color:"#fff", fontWeight:700, fontSize:15,
 cursor:loader||disabled?"not-allowed":"pointer", fontFamily:"'Inter',system-ui,sans-serif",
 transition:"all 0.25s", opacity:disabled?0.5:1,
 boxShadow:!disabled&&!loader ? "0 8px 24px rgba(63,212,199,0.25)" : "none",
 letterSpacing:"0.02em" }}>
 {loader ? (
 <span style={{ display:"inline-flex", alignItems:"center", gap:8, justifyContent:"center" }}> <span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,0.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin 0.7s linear infinite", display:"inline-block" }} />
 Logger ind...
 </span>
 ) : tekst}
 </button>
 );
 const tilbage_knap = (onClick, tekst) => (
 <button type="button" onClick={onClick}
 onMouseEnter={e => e.currentTarget.style.color = T.creme}
 onMouseLeave={e => e.currentTarget.style.color = "rgba(148,163,184,0.8)"}
 style={{ width:"100%", background:"none", border:"none", color:"rgba(148,163,184,0.8)", fontSize:12, marginTop:12, padding:"6px 0", cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif", transition:"color 0.2s" }}>{tekst || "← Tilbage til login"}</button>
 );
 const trin_indikator = (aktivt, total) => (
 <div style={{ display:"flex", justifyContent:"center", gap:6, marginBottom:16 }}>
 {Array.from({length:total}, (_,i) => (
 <div key={i} style={{ width:i+1===aktivt?24:8, height:8, borderRadius:4, background:i+1<=aktivt?T.turkis:"rgba(255,255,255,0.15)", transition:"all 0.3s" }} />
 ))}
 </div>
 );
 // ── GEM FINGERAFTRYK DIALOG (vises efter vellykket login på native Android) ──
 if (vis_gem_biometri) {
  return (
   <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24, position:"relative", overflow:"hidden",
   background:"linear-gradient(135deg, #050816 0%, #0c0f2e 40%, #0a0c20 70%, #060818 100%)" }}>
    <div style={{ width:"100%", maxWidth:420, position:"relative", zIndex:1 }}>
     <div style={{ background:"rgba(6,9,26,0.92)", border:"1px solid rgba(63,212,199,0.2)", borderRadius:20, padding:"clamp(20px,5vw,36px) clamp(16px,4vw,32px)", backdropFilter:"blur(28px)", WebkitBackdropFilter:"blur(28px)" }}>
      <div style={{ textAlign:"center", marginBottom:20 }}>
       <div style={{ fontSize:48, marginBottom:12 }}>&#x1F44D;</div>
       <div style={{ fontSize:16, fontWeight:700, color:"#e2e8f0", marginBottom:8 }}>Login lykkedes!</div>
       <div style={{ fontSize:13, color:"rgba(148,163,184,0.8)", lineHeight:1.6 }}>
        Vil du aktivere fingeraftryk-login til næste gang?<br />
        Dine oplysninger gemmes krypteret på din enhed.
       </div>
      </div>
      <button
       onClick={() => gem_til_biometri(true)}
       disabled={gem_biometri_loader}
       style={{ width:"100%", background:"linear-gradient(135deg,#4f46e5 0%,#3fd4c7 100%)", border:"none", borderRadius:11, padding:"15px", color:"#fff", fontWeight:700, fontSize:15, cursor: gem_biometri_loader ? "not-allowed" : "pointer", fontFamily:"'Inter',system-ui,sans-serif", marginBottom:10, opacity: gem_biometri_loader ? 0.6 : 1 }}
      >
       {gem_biometri_loader ? "Gemmer..." : "Ja, aktiver fingeraftryk-login"}
      </button>
      <button
       onClick={() => gem_til_biometri(false)}
       style={{ width:"100%", background:"none", border:"1px solid rgba(255,255,255,0.1)", borderRadius:11, padding:"13px", color:"rgba(148,163,184,0.8)", fontWeight:500, fontSize:13, cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif" }}
      >
       Nej tak, brug adgangskode
      </button>
     </div>
    </div>
   </div>
  );
 }

 return (
 <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24, position:"relative", overflow:"hidden",
 background:"linear-gradient(135deg, #050816 0%, #0c0f2e 40%, #0a0c20 70%, #060818 100%)" }}>
 {/* Baggrunds-glød — store animerede orbs + holografisk mesh */}
 <div className="sv-holo-mesh" style={{ position:"absolute", inset:0 }} /> <div className="sv-orb sv-orb-1" style={{ opacity:0.28 }} /> <div className="sv-orb sv-orb-2" style={{ opacity:0.22 }} /> <div className="sv-orb sv-orb-3" style={{ opacity:0.18 }} /> <div className="sv-orb sv-orb-4" style={{ opacity:0.14 }} /> <div className="sv-scanlines" style={{ position:"absolute" }} />
 {/* Sprint H: Theme toggle tilgængelig før login */}
 <div style={{ position:"absolute", top:16, right:16, zIndex: 10 }}> <ThemeToggle /> </div> <div style={{ width:"100%", maxWidth:420, position:"relative", zIndex:1 }}>
 {/* Logo */}
 <div style={{ textAlign:"center", marginBottom:40 }}> <div style={{ display:"inline-block", position:"relative", marginBottom:20 }}> <div style={{ position:"absolute", inset:"-12px", borderRadius:"50%", background:"radial-gradient(circle, rgba(63,212,199,0.15) 0%, transparent 70%)", filter:"blur(8px)" }} /> <img src={LOGO_SRC} alt="SkilsmisseVejen logo" style={{ height:"clamp(48px, 10vw, 72px)", position:"relative" }} /> </div> <div style={{ fontSize:28, fontWeight:200, letterSpacing:"0.05em" }}> <span style={{ color:T.creme }}>Skilsmisse</span><span className="sv-gradient-text" style={{ fontWeight:700 }}>Vejen</span> </div> <div style={{ fontFamily:"Clicker Script, cursive", fontSize:18, color:T.dæmpet, marginTop:4 }}>Du er ikke alene</div> <div style={{ display:"inline-block", fontSize:10, color:T.subtil, marginTop:10, letterSpacing:"0.15em", textTransform:"uppercase",
 background:"rgba(63,212,199,0.08)", border:"1px solid rgba(63,212,199,0.2)", borderRadius:20, padding:"3px 12px" }}>
 Sagsportal
 </div> </div>
 {/* ── LOGIN FORMULAR ── */}
 {!trin && (
 <div style={{ ...panel, position:"relative", overflow:"hidden", borderTop:"none", boxShadow:"0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06) inset, 0 0 80px rgba(63,212,199,0.05)" }}>
 {/* Gradient accent strip */}
 <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${T.turkis}, #a78bfa, #6366f1, transparent)`, borderRadius:"20px 20px 0 0", pointerEvents:"none" }} />
 {/* Glow orbs inside card */}
 <div style={{ position:"absolute", top:-30, right:-20, width:150, height:150, borderRadius:"50%", background:`${T.turkis}06`, filter:"blur(30px)", pointerEvents:"none" }} /> <div style={{ position:"absolute", bottom:-20, left:-10, width:120, height:120, borderRadius:"50%", background:"rgba(99,102,241,0.06)", filter:"blur(25px)", pointerEvents:"none" }} /> <div style={{ fontSize:14, fontWeight:500, color:T.creme, marginBottom:20, textAlign:"center", position:"relative" }}>Log ind på din konto</div>
 {fejl_boks}
 <div style={{ marginBottom:14 }}> <label htmlFor="email-felt" style={{ fontSize:12, color:T.dæmpet, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:7, display:"block" }}>Email</label> <input id="email-felt" type="email" value={email} onChange={e=>{setEmail(e.target.value);setFejl("");}}
 onKeyDown={e=>e.key==="Enter"&&document.getElementById("pw-felt")?.focus()}
 onAnimationStart={e=>{
   if (e.animationName === 'sv-autofill-start') {
     setEmail(e.target.value);
   }
 }}
 placeholder="din@skilsmissevej.dk" autoComplete="email"
 autoFocus
 className="sv-input"
 style={{ width:"100%", background:"rgba(8,12,30,0.65)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, padding:"13px 15px", color:T.creme, fontSize:14, fontFamily:"'Inter',system-ui,sans-serif", boxSizing:"border-box", transition:"all 0.25s cubic-bezier(0.4,0,0.2,1)" }} /> </div> <div style={{ marginBottom:20 }}> <label htmlFor="pw-felt" style={{ fontSize:12, color:T.dæmpet, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:7, display:"block" }}>Adgangskode</label> <div style={{ position:"relative" }}> <input id="pw-felt" type={vis_pw?"text":"password"} value={adgangskode}
 onChange={e=>{setAdgangskode(e.target.value);setFejl("");}}
 onKeyDown={e=>e.key==="Enter"&&forsøg_login()}
 onAnimationStart={e=>{
   if (e.animationName === 'sv-autofill-start') {
     setAdgangskode(e.target.value);
   }
 }}
 placeholder="Din adgangskode" autoComplete="current-password"
 className="sv-input"
 style={{ width:"100%", background:"rgba(8,12,30,0.65)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, padding:"13px 44px 13px 15px", color:T.creme, fontSize:14, fontFamily:"'Inter',system-ui,sans-serif", boxSizing:"border-box", transition:"all 0.25s cubic-bezier(0.4,0,0.2,1)" }} /> <button
 onClick={()=>setVisPw(p=>!p)}
 aria-label={vis_pw ? "Skjul adgangskode" : "Vis adgangskode"}
 type="button"
 style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:T.dæmpet, fontSize:16, cursor:"pointer" }}>
 {vis_pw?"":""}
 </button> </div> </div>
 {turkis_knap(
 "Log ind →",
 forsøg_login,
 !email||!adgangskode,
 !email && !adgangskode ? "Udfyld email og adgangskode"
 : !email ? "Udfyld din email"
 : "Udfyld din adgangskode"
 )}
 {/* Fingeraftryk-knap — native Android (Capacitor APK):
     Vises kun hvis (1) plugin er bekræftet installeret (er_native_plugin_klar)
     OG (2) credentials er gemt i Keystore (native_biometri_klar).
     I APKs uden plugin (v1.2.0): er_native_plugin_klar = false → knap vises aldrig.
     Passkey-knap (WebAuthn) vises på desktop/web-browser som fallback. */}
 {er_native_plugin_klar ? (
  native_biometri_klar && (
   <div style={{ marginTop:12 }}>
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
     <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.08)" }} />
     <span style={{ fontSize:11, color:T.dæmpet, letterSpacing:"0.08em", textTransform:"uppercase", whiteSpace:"nowrap" }}>eller</span>
     <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.08)" }} />
    </div>
    <button
     onClick={forsøg_biometri_login}
     disabled={passkey_loader || loader}
     type="button"
     aria-label="Log ind med fingeraftryk"
     style={{
      width:"100%",
      background: passkey_loader ? "rgba(255,255,255,0.04)" : "rgba(63,212,199,0.08)",
      border:"1px solid rgba(63,212,199,0.25)",
      borderRadius:11, padding:"13px",
      color: passkey_loader ? T.dæmpet : T.turkis,
      fontWeight:600, fontSize:14,
      cursor: passkey_loader ? "not-allowed" : "pointer",
      fontFamily:"'Inter',system-ui,sans-serif",
      display:"flex", alignItems:"center", justifyContent:"center", gap:10,
      transition:"all 0.25s",
     }}
    >
     {passkey_loader ? (
      <span style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
       <span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,0.2)", borderTopColor:T.turkis, borderRadius:"50%", animation:"spin 0.7s linear infinite", display:"inline-block" }} />
       Afventer fingeraftryk...
      </span>
     ) : (
      <span>Log ind med fingeraftryk</span>
     )}
    </button>
    <div style={{ fontSize:11, color:T.dæmpet, textAlign:"center", marginTop:6 }}>
     Ingen adgangskode nødvendig
    </div>
   </div>
  )
 ) : (
  /* Web-browser fallback: WebAuthn passkey */
  passkey_understoettes && (
   <div style={{ marginTop:12 }}>
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
     <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.08)" }} />
     <span style={{ fontSize:11, color:T.dæmpet, letterSpacing:"0.08em", textTransform:"uppercase", whiteSpace:"nowrap" }}>eller</span>
     <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.08)" }} />
    </div>
    <button
     onClick={forsøg_passkey_login}
     disabled={passkey_loader || loader}
     type="button"
     style={{
      width:"100%",
      background: passkey_loader ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.06)",
      border:"1px solid rgba(255,255,255,0.12)",
      borderRadius:11, padding:"13px",
      color: passkey_loader ? T.dæmpet : T.creme,
      fontWeight:600, fontSize:14,
      cursor: passkey_loader ? "not-allowed" : "pointer",
      fontFamily:"'Inter',system-ui,sans-serif",
      display:"flex", alignItems:"center", justifyContent:"center", gap:10,
      transition:"all 0.25s",
     }}
    >
     {passkey_loader ? (
      <span style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
       <span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,0.2)", borderTopColor:T.creme, borderRadius:"50%", animation:"spin 0.7s linear infinite", display:"inline-block" }} />
       Afventer fingeraftryk...
      </span>
     ) : (
      <span>Log ind med fingeraftryk / Face ID</span>
     )}
    </button>
    <div style={{ fontSize:11, color:T.dæmpet, textAlign:"center", marginTop:6 }}>
     Ingen adgangskode nødvendig
    </div>
   </div>
  )
 )}
 <div style={{ textAlign:"center", marginTop:16, fontSize:12, color:T.dæmpet }}> <button
 onClick={() => setTrin('reset_anmod')}
 type="button"
 style={{ background:"none", border:"none", color:T.turkis, fontSize:12, cursor:"pointer", textDecoration:"underline", padding:0, fontFamily:"'Inter',system-ui,sans-serif" }}>
 Glemt adgangskode?
 </button> </div> </div>
 )}
 {/* ── 2FA SETUP: Vælg metode ── */}
 {trin === 'setup_valg' && (
 <div style={panel}>
 {trin_indikator(1, 3)}
 <div style={{ textAlign:"center", marginBottom:20 }}> <div style={{ fontSize:36, marginBottom:8 }}></div> <div style={{ fontSize:15, fontWeight:600, color:T.creme, marginBottom:6 }}>Opsæt to-faktor login</div> <div style={{ fontSize:12, color:T.dæmpet, lineHeight:1.5 }}>Din administrator kræver ekstra sikkerhed på din konto. Vælg hvordan du vil bekræfte dit login:</div> </div>
 {fejl_boks}
 <button onClick={()=>start_setup('app')} style={{ width:"100%", padding:"16px 18px", background:"rgba(63,212,199,0.08)", border:`1px solid ${T.turkis}44`, borderRadius:10, marginBottom:10, cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:14 }}> <div style={{ fontSize:28 }}></div> <div><div style={{ fontSize:14, fontWeight:600, color:T.creme }}>Authenticator-app</div><div style={{ fontSize:12, color:T.dæmpet, marginTop:2 }}>Microsoft Authenticator, Google Authenticator el.lign.</div></div> </button> <button onClick={()=>start_setup('email')} style={{ width:"100%", padding:"16px 18px", background:"rgba(255,255,255,0.04)", border:T.skillelinje, borderRadius:10, cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:14 }}> <div style={{ fontSize:28 }}></div> <div><div style={{ fontSize:14, fontWeight:600, color:T.creme }}>Email-kode</div><div style={{ fontSize:12, color:T.dæmpet, marginTop:2 }}>En 6-cifret kode sendes til din email ved hvert login</div></div> </button>
 {tilbage_knap(nulstil)}
 </div>
 )}
 {/* ── 2FA SETUP: Scan QR (app) ── */}
 {trin === 'setup_app' && (
 <div style={panel}>
 {trin_indikator(2, 3)}
 <div style={{ textAlign:"center", marginBottom:16 }}> <div style={{ fontSize:14, fontWeight:600, color:T.creme, marginBottom:8 }}>Scan QR-koden</div> <div style={{ fontSize:12, color:T.dæmpet }}>Åbn din authenticator-app og scan koden nedenfor</div> </div>
 {fejl_boks}
 {qr_data?.qr_data_url && (
 <div style={{ textAlign:"center", marginBottom:16 }}> <img src={qr_data.qr_data_url} alt="QR-kode" style={{ width:220, height:220, borderRadius:12, background:"#fff", padding:8 }} /> </div>
 )}
 {qr_data?.hemmelighed_visning && (
 <div style={{ background:"rgba(8,12,30,0.6)", border:"1px solid rgba(63,212,199,0.15)", borderRadius:8, padding:"10px 14px", marginBottom:16, textAlign:"center", backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)" }}> <div style={{ fontSize:12, color:T.dæmpet, marginBottom:4 }}>Kan ikke scanne? Indtast manuelt:</div> <div style={{ fontSize:13, fontFamily:"DM Mono, monospace", color:T.turkis, letterSpacing:"0.1em", wordBreak:"break-all" }}>{qr_data.hemmelighed_visning}</div> </div>
 )}
 <div style={{ fontSize:12, color:T.dæmpet, marginBottom:12, textAlign:"center" }}>Indtast den 6-cifrede kode fra appen:</div>
 {kode_input(bekræft_setup)}
 {turkis_knap("Bekræft →", bekræft_setup, to_fa_kode.length!==6)}
 {tilbage_knap(()=>{setTrin('setup_valg');setToFaKode("");setFejl("");}, "← Vælg anden metode")}
 </div>
 )}
 {/* ── 2FA SETUP: Email-kode ── */}
 {trin === 'setup_email' && (
 <div style={panel}>
 {trin_indikator(2, 3)}
 <div style={{ textAlign:"center", marginBottom:16 }}> <div style={{ fontSize:36, marginBottom:8 }}></div> <div style={{ fontSize:14, fontWeight:600, color:T.creme, marginBottom:6 }}>Bekræft med email-kode</div> <div style={{ fontSize:12, color:T.dæmpet }}>En kode er sendt til <strong style={{color:T.creme}}>{email_maskeret}</strong></div> </div>
 {fejl_boks}
 {kode_input(bekræft_setup)}
 {turkis_knap("Bekræft →", bekræft_setup, to_fa_kode.length!==6)}
 {tilbage_knap(()=>{setTrin('setup_valg');setToFaKode("");setFejl("");}, "← Vælg anden metode")}
 </div>
 )}
 {/* ── RECOVERY-KODER (vis efter setup) ── */}
 {trin === 'recovery_koder' && (
 <div style={panel}>
 {trin_indikator(3, 3)}
 <div style={{ textAlign:"center", marginBottom:16 }}> <div style={{ fontSize:36, marginBottom:8 }}></div> <div style={{ fontSize:15, fontWeight:600, color:T.creme, marginBottom:6 }}>2FA er aktiveret!</div> <div style={{ fontSize:12, color:T.dæmpet }}>Gem disse recovery-koder et sikkert sted. De kan bruges hvis du mister adgang til din app eller email.</div> </div> <div style={{ background:"rgba(255,107,107,0.06)", border:"1px solid rgba(255,107,107,0.2)", borderRadius:10, padding:"14px 18px", marginBottom:16 }}> <div style={{ fontSize:12, color:T.roed, fontWeight:600, marginBottom:10, textAlign:"center" }}>Disse koder kan ikke vises igen</div> <div className="sv-form-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 20px" }}>
 {recovery_koder.map((kode, i) => (
 <div key={i} style={{ fontFamily:"DM Mono, monospace", fontSize:14, color:T.creme, padding:"4px 0" }}>{i+1}. {kode}</div>
 ))}
 </div> </div> <div style={{ display:"flex", gap:8, marginBottom:16 }}> <button
 onClick={async ()=>{
 try {
 await navigator.clipboard?.writeText(recovery_koder.join('\n'));
 setKopieret(true);
 setTimeout(() => setKopieret(false), 3000);
 } catch(e) {
 setFejl("Kunne ikke kopiere — kopiér manuelt fra listen ovenfor");
 }
 }}
 style={{ flex:1, padding:"10px", borderRadius:8,
 border:`1px solid ${kopieret ? "rgba(16,185,129,0.4)" : T.turkis+"33"}`,
 background: kopieret ? "rgba(16,185,129,0.1)" : "transparent",
 color: kopieret ? "#10b981" : T.turkis,
 fontSize:12, cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif",
 transition:"all 0.3s" }}>
 {kopieret ? " Kopieret!" : "Kopiér alle"}
 </button> <button onClick={()=>{const b=new Blob([recovery_koder.map((k,i)=>(i+1)+'. '+k).join('\n')],{type:'text/plain'});const a=document.createElement('a');a.href=URL.createObjectURL(b);a.download='recovery-koder.txt';a.click();}}
 style={{ flex:1, padding:"10px", borderRadius:8, border:`1px solid ${T.turkis}33`, background:"transparent", color:T.turkis, fontSize:12, cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif" }}>
 Download
 </button> </div>
 {turkis_knap("Fortsæt til systemet →", ()=>onLogin(JSON.parse(sessionStorage.getItem("sv_bruger"))))}
 </div>
 )}
 {/* ── PASSWORD RESET: Anmod ── */}
 {trin === 'reset_anmod' && (
 <div style={panel}> <div style={{ textAlign:"center", marginBottom:20 }}> <div style={{ fontSize:36, marginBottom:8 }}></div> <div style={{ fontSize:15, fontWeight:600, color:T.creme, marginBottom:6 }}>Nulstil adgangskode</div> <div style={{ fontSize:12, color:T.dæmpet, lineHeight:1.5 }}>Skriv din email — du modtager et link til at nulstille din adgangskode.</div> </div>
 {fejl_boks}
 <div style={{ marginBottom:14 }}> <label style={{ fontSize:12, color:T.dæmpet, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:7, display:"block" }}>Email</label> <input type="email" value={email} onChange={e=>setEmail(e.target.value)} autoFocus
 placeholder="din@skilsmissevej.dk" autoComplete="email"
 className="sv-input"
 style={{ width:"100%", background:"rgba(8,12,30,0.65)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, padding:"13px 15px", color:T.creme, fontSize:14, fontFamily:"'Inter',system-ui,sans-serif", boxSizing:"border-box", transition:"all 0.25s cubic-bezier(0.4,0,0.2,1)" }} /> </div>
 {turkis_knap("Send nulstillingslink", send_reset_email, !email)}
 {tilbage_knap(nulstil)}
 </div>
 )}
 {/* ── PASSWORD RESET: Link sendt ── */}
 {trin === 'reset_sendt' && (
 <div style={panel}> <div style={{ textAlign:"center" }}> <div style={{ fontSize:36, marginBottom:12 }}></div> <div style={{ fontSize:15, fontWeight:600, color:T.creme, marginBottom:8 }}>Link sendt!</div> <div style={{ fontSize:12, color:T.dæmpet, lineHeight:1.6 }}>Tjek din email. Linket er gyldigt i 30 minutter.</div> </div> <div style={{ marginTop:20 }}>{tilbage_knap(nulstil, "← Tilbage til login")}</div> </div>
 )}
 {/* ── 2FA LOGIN: Vælg metode + verificer ── */}
 {trin === '2fa' && (
 <div style={panel}> <div style={{ textAlign:"center", marginBottom:16 }}> <div style={{ fontSize:36, marginBottom:8 }}></div> <div style={{ fontSize:14, fontWeight:500, color:T.creme, marginBottom:6 }}>To-faktor bekræftelse</div> </div>
 {fejl_boks}
 {/* Metode-vælger */}
 <div style={{ display:"flex", gap:6, marginBottom:16 }}>
 {[{id:'app',label:'App',ikon:''},{id:'email',label:'Email',ikon:''},{id:'recovery',label:'Recovery',ikon:''}].map(m => (
 <button key={m.id} onClick={()=>{setValgt2faMetode(m.id);setToFaKode("");setFejl("");setEmailMaskeret("");}}
 style={{ flex:1, padding:"10px 6px", borderRadius:8, border:`1px solid ${valgt_2fa_metode===m.id?T.turkis+"66":"rgba(255,255,255,0.1)"}`, background:valgt_2fa_metode===m.id?"rgba(63,212,199,0.1)":"transparent", color:valgt_2fa_metode===m.id?T.turkis:T.dæmpet, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',system-ui,sans-serif", textAlign:"center" }}> <div style={{fontSize:18,marginBottom:2}}>{m.ikon}</div>{m.label}
 </button>
 ))}
 </div>
 {valgt_2fa_metode === 'app' && <div style={{fontSize:12,color:T.dæmpet,marginBottom:12,textAlign:"center"}}>Indtast koden fra din authenticator-app</div>}
 {valgt_2fa_metode === 'email' && !email_maskeret && (
 <div style={{ marginBottom:12 }}> <div style={{fontSize:12,color:T.dæmpet,marginBottom:10,textAlign:"center"}}>Send en engangskode til din tilknyttede email-adresse.</div>
 {turkis_knap("Send kode til email", send_email_kode, false)}
 </div>
 )}
 {valgt_2fa_metode === 'email' && email_maskeret && (
 <div style={{fontSize:12,color:T.dæmpet,marginBottom:12,textAlign:"center"}}>
 Kode sendt til <strong style={{color:T.creme}}>{email_maskeret}</strong> <button onClick={()=>{setEmailMaskeret("");setToFaKode("");}} type="button" style={{background:"none",border:"none",color:T.turkis,fontSize:11,cursor:"pointer",marginLeft:8,fontFamily:"'Inter',system-ui,sans-serif"}}>Send igen</button> </div>
 )}
 {valgt_2fa_metode === 'recovery' && <div style={{fontSize:12,color:T.dæmpet,marginBottom:12,textAlign:"center"}}>Indtast en af dine recovery-koder (8 tegn)</div>}
 {(valgt_2fa_metode !== 'email' || email_maskeret) && kode_input(bekræft_2fa)}
 {(valgt_2fa_metode !== 'email' || email_maskeret) && turkis_knap("Bekræft →", bekræft_2fa, valgt_2fa_metode === 'recovery' ? to_fa_kode.length < 8 : to_fa_kode.length < 6)}
 {tilbage_knap(nulstil)}
 </div>
 )}
 </div> </div>
 );
}
// ─────────────────────────────────────────────────────────
export { LoginSkærm };